<template>
  <div class="push-page">
    <!-- Analytics Modal -->
    <div v-if="showAnalyticsModal" class="modal-backdrop" @click.self="showAnalyticsModal = false">
      <div class="modal modal-analytics">
        <div class="modal-header">
          <div>
            <h2 class="modal-title">{{ $t('admin.notifications.analytics_title') }}</h2>
            <p class="modal-subtitle">{{ analyticsData?.title }}</p>
          </div>
          <button class="modal-close" @click="showAnalyticsModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="analyticsLoading" class="analytics-loading">
            <span class="spinner"></span>
            {{ $t('admin.notifications.loading_analytics') }}
          </div>
          <template v-else-if="analyticsData">
            <div class="analytics-grid">
              <!-- Sent -->
              <div class="analytics-card">
                <div class="analytics-count">{{ analyticsData.sent_count.toLocaleString() }}</div>
                <div class="analytics-label">{{ $t('admin.notifications.stat_sent') }}</div>
                <div class="analytics-rate analytics-rate-neutral">—</div>
              </div>
              <!-- Delivered -->
              <div class="analytics-card">
                <div class="analytics-count">{{ analyticsData.delivered_count.toLocaleString() }}</div>
                <div class="analytics-label">{{ $t('admin.notifications.stat_delivered') }}</div>
                <div class="analytics-rate" :class="rateColorClass(analyticsData.delivery_rate)">
                  {{ analyticsData.delivery_rate.toFixed(1) }}%
                </div>
              </div>
              <!-- Opened -->
              <div class="analytics-card">
                <div class="analytics-count">{{ analyticsData.opened_count.toLocaleString() }}</div>
                <div class="analytics-label">{{ $t('admin.notifications.stat_opened') }}</div>
                <div class="analytics-rate" :class="rateColorClass(analyticsData.open_rate)">
                  {{ analyticsData.open_rate.toFixed(1) }}%
                </div>
              </div>
              <!-- Clicked -->
              <div class="analytics-card">
                <div class="analytics-count">{{ analyticsData.clicked_count.toLocaleString() }}</div>
                <div class="analytics-label">{{ $t('admin.notifications.stat_clicked') }}</div>
                <div class="analytics-rate" :class="rateColorClass(analyticsData.click_through_rate)">
                  {{ analyticsData.click_through_rate.toFixed(1) }}%
                </div>
              </div>
            </div>
            <div class="analytics-legend">
              <span class="legend-dot legend-green"></span> &gt;50%
              <span class="legend-dot legend-yellow"></span> &gt;20%
              <span class="legend-dot legend-red"></span> &lt;20%
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn btn-ghost" @click="showAnalyticsModal = false">{{ $t('admin.notifications.btn_close') }}</button>
        </div>
      </div>
    </div>

    <!-- Compose Modal -->
    <div v-if="showComposeModal" class="modal-backdrop" @click.self="closeComposeModal">
      <div class="modal">
        <div class="modal-header">
          <h2 class="modal-title">{{ $t('admin.notifications.compose_title') }}</h2>
          <button class="modal-close" @click="closeComposeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.notifications.field_title') }}</label>
            <input
              v-model="form.title"
              type="text"
              class="form-input"
              :placeholder="$t('admin.notifications.field_title_placeholder')"
              maxlength="255"
            >
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.notifications.field_body') }}</label>
            <textarea
              v-model="form.body"
              class="form-textarea"
              rows="4"
              :placeholder="$t('admin.notifications.field_body_placeholder')"
              maxlength="2000"
            />
          </div>

          <div class="form-section-title">{{ $t('admin.notifications.section_audience') }}</div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">{{ $t('admin.notifications.field_vip_min') }}</label>
              <select v-model="form.vip_level_min" class="form-select" @change="debouncedEstimate">
                <option :value="null">{{ $t('admin.notifications.filter_any') }}</option>
                <option v-for="l in vipLevels" :key="l.value" :value="l.value">{{ l.label }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">{{ $t('admin.notifications.field_vip_max') }}</label>
              <select v-model="form.vip_level_max" class="form-select" @change="debouncedEstimate">
                <option :value="null">{{ $t('admin.notifications.filter_any') }}</option>
                <option v-for="l in vipLevels" :key="l.value" :value="l.value">{{ l.label }}</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('admin.notifications.field_activity_status') }}</label>
            <select v-model="form.activity_status" class="form-select" @change="debouncedEstimate">
              <option value="">{{ $t('admin.notifications.activity_all') }}</option>
              <option value="active">{{ $t('admin.notifications.activity_active') }}</option>
              <option value="inactive">{{ $t('admin.notifications.activity_inactive') }}</option>
              <option value="dormant">{{ $t('admin.notifications.activity_dormant') }}</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('admin.notifications.field_countries') }}</label>
            <input
              v-model="form.countries_raw"
              type="text"
              class="form-input"
              :placeholder="$t('admin.notifications.field_countries_placeholder')"
              @input="debouncedEstimate"
            >
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('admin.notifications.field_schedule') }}</label>
            <input
              v-model="form.scheduled_at"
              type="datetime-local"
              class="form-input"
            >
          </div>

          <div class="audience-estimate">
            <span class="audience-icon">👥</span>
            <span v-if="estimating" class="audience-value">{{ $t('admin.notifications.estimating') }}</span>
            <span v-else class="audience-value">
              ~{{ estimatedAudience.toLocaleString() }} {{ $t('admin.notifications.players') }}
            </span>
            <span class="audience-label">{{ $t('admin.notifications.estimated_audience') }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-ghost" @click="closeComposeModal">{{ $t('admin.notifications.btn_cancel') }}</button>
          <button
            class="btn btn-primary"
            :disabled="saving || !form.title.trim() || !form.body.trim()"
            @click="saveCampaign"
          >
            <span v-if="saving" class="spinner-xs"></span>
            {{ $t('admin.notifications.btn_save_draft') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Confirm Send Modal -->
    <div v-if="showSendModal" class="modal-backdrop" @click.self="showSendModal = false">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h2 class="modal-title">{{ $t('admin.notifications.send_title') }}</h2>
          <button class="modal-close" @click="showSendModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            {{ $t('admin.notifications.send_confirm', { title: campaignToSend?.title, count: sendEstimate.toLocaleString() }) }}
          </p>
          <p class="confirm-warning">{{ $t('admin.notifications.send_warning') }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-ghost" @click="showSendModal = false">{{ $t('admin.notifications.btn_cancel') }}</button>
          <button
            class="btn btn-primary"
            :disabled="sending"
            @click="confirmSend"
          >
            <span v-if="sending" class="spinner-xs"></span>
            {{ $t('admin.notifications.btn_send_now') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.notifications.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.notifications.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="openComposeModal">{{ $t('admin.notifications.btn_new_campaign') }}</button>
      </div>
    </div>

    <!-- Stat Cards -->
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-value">{{ stats.total.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.notifications.stat_total') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.sent.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.notifications.stat_sent') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.draft.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.notifications.stat_drafts') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.totalDelivered.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.notifications.stat_total_delivered') }}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <select v-model="filterStatus" class="filter-select" @change="fetchCampaigns(1)">
        <option value="">{{ $t('admin.notifications.filter_all_statuses') }}</option>
        <option value="draft">{{ $t('admin.notifications.status_draft') }}</option>
        <option value="sending">{{ $t('admin.notifications.status_sending') }}</option>
        <option value="sent">{{ $t('admin.notifications.status_sent') }}</option>
        <option value="failed">{{ $t('admin.notifications.status_failed') }}</option>
      </select>
    </div>

    <!-- Campaigns Table -->
    <div class="table-container">
      <div v-if="loading" class="table-loading">
        <span class="spinner"></span>
        {{ $t('admin.notifications.loading') }}
      </div>

      <template v-else>
        <table>
          <thead>
            <tr>
              <th>{{ $t('admin.notifications.col_title') }}</th>
              <th>{{ $t('admin.notifications.col_status') }}</th>
              <th>{{ $t('admin.notifications.col_sent') }}</th>
              <th>{{ $t('admin.notifications.col_delivered') }}</th>
              <th>{{ $t('admin.notifications.col_opened') }}</th>
              <th>{{ $t('admin.notifications.col_created') }}</th>
              <th>{{ $t('admin.notifications.col_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="campaigns.length > 0">
              <tr v-for="campaign in campaigns" :key="campaign.id">
                <td>
                  <strong class="campaign-title">{{ campaign.title }}</strong>
                  <div class="campaign-body">{{ truncate(campaign.body, 60) }}</div>
                </td>
                <td>
                  <span class="badge" :class="statusBadgeClass(campaign.status)">
                    {{ campaign.status }}
                  </span>
                </td>
                <td class="td-num">{{ campaign.sent_count.toLocaleString() }}</td>
                <td class="td-num">{{ campaign.delivered_count.toLocaleString() }}</td>
                <td class="td-num">{{ campaign.opened_count.toLocaleString() }}</td>
                <td class="td-date">{{ formatDate(campaign.created_at) }}</td>
                <td>
                  <div class="action-buttons">
                    <button
                      v-if="campaign.status === 'draft'"
                      class="btn-action btn-action-send"
                      title="Send"
                      @click="openSendModal(campaign)"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </button>
                    <button
                      v-if="campaign.status === 'draft'"
                      class="btn-action btn-action-danger"
                      title="Delete"
                      :disabled="deletingId === campaign.id"
                      @click="deleteCampaign(campaign)"
                    >
                      <span v-if="deletingId === campaign.id" class="spinner-xs spinner-xs-danger"></span>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                    <button
                      v-if="campaign.status === 'sent'"
                      class="btn-action btn-action-analytics"
                      title="View Analytics"
                      @click="openAnalyticsModal(campaign)"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="20" x2="18" y2="10" />
                        <line x1="12" y1="20" x2="12" y2="4" />
                        <line x1="6" y1="20" x2="6" y2="14" />
                      </svg>
                    </button>
                    <span v-if="campaign.status !== 'draft' && campaign.status !== 'sent'" class="text-muted">—</span>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="7" class="empty-row">{{ $t('admin.notifications.empty') }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="meta.last_page > 1" class="pagination">
          <button
            class="pagination-btn"
            :disabled="meta.current_page <= 1"
            @click="fetchCampaigns(meta.current_page - 1)"
          >
            &larr;
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === meta.current_page }"
            @click="fetchCampaigns(page)"
          >
            {{ page }}
          </button>
          <span class="pagination-info">
            {{ $t('admin.notifications.showing', { start: (meta.current_page - 1) * meta.per_page + 1, end: Math.min(meta.current_page * meta.per_page, meta.total), total: meta.total }) }}
          </span>
          <button
            class="pagination-btn"
            :disabled="meta.current_page >= meta.last_page"
            @click="fetchCampaigns(meta.current_page + 1)"
          >
            &rarr;
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

interface PushCampaign {
  id: number
  title: string
  body: string
  segment_rules: Record<string, unknown> | null
  status: string
  sent_count: number
  delivered_count: number
  opened_count: number
  clicked_count: number
  scheduled_at: string | null
  sent_at: string | null
  created_at: string | null
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

interface Stats {
  total: number
  sent: number
  draft: number
  totalDelivered: number
}

interface CampaignAnalytics {
  id: number
  title: string
  sent_count: number
  delivered_count: number
  opened_count: number
  clicked_count: number
  delivery_rate: number
  open_rate: number
  click_through_rate: number
}

interface ComposeForm {
  title: string
  body: string
  vip_level_min: number | null
  vip_level_max: number | null
  activity_status: string
  countries_raw: string
  scheduled_at: string
}

const { t } = useI18n()
const toast = useToastStore()

// Table state
const campaigns = ref<PushCampaign[]>([])
const loading = ref(false)
const meta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 })
const filterStatus = ref('')

// Stats
const stats = ref<Stats>({ total: 0, sent: 0, draft: 0, totalDelivered: 0 })

// Compose modal state
const showComposeModal = ref(false)
const saving = ref(false)
const estimating = ref(false)
const estimatedAudience = ref(0)
let estimateTimer: ReturnType<typeof setTimeout> | null = null

const form = ref<ComposeForm>({
  title: '',
  body: '',
  vip_level_min: null,
  vip_level_max: null,
  activity_status: '',
  countries_raw: '',
  scheduled_at: '',
})

// Send confirm modal
const showSendModal = ref(false)
const campaignToSend = ref<PushCampaign | null>(null)
const sendEstimate = ref(0)
const sending = ref(false)

// Row action state
const deletingId = ref<number | null>(null)

// Analytics modal state
const showAnalyticsModal = ref(false)
const analyticsLoading = ref(false)
const analyticsData = ref<CampaignAnalytics | null>(null)

const vipLevels = computed(() => [
  { value: 0, label: t('admin.notifications.vip_bronze') },
  { value: 1, label: t('admin.notifications.vip_silver') },
  { value: 2, label: t('admin.notifications.vip_gold') },
  { value: 3, label: t('admin.notifications.vip_platinum') },
  { value: 4, label: t('admin.notifications.vip_diamond') },
])

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = meta.value.last_page
  const current = meta.value.current_page
  const start = Math.max(1, current - 2)
  const end = Math.min(total, current + 2)
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// ---------------------------------------------------------------
// Compose modal helpers
// ---------------------------------------------------------------

function openComposeModal() {
  form.value = {
    title: '',
    body: '',
    vip_level_min: null,
    vip_level_max: null,
    activity_status: '',
    countries_raw: '',
    scheduled_at: '',
  }
  estimatedAudience.value = 0
  showComposeModal.value = true
  fetchEstimate()
}

function closeComposeModal() {
  showComposeModal.value = false
}

function buildSegmentRules(): Record<string, unknown> | null {
  const rules: Record<string, unknown> = {}

  if (form.value.vip_level_min !== null) rules.vip_level_min = form.value.vip_level_min
  if (form.value.vip_level_max !== null) rules.vip_level_max = form.value.vip_level_max
  if (form.value.activity_status) rules.activity_status = form.value.activity_status

  const countries = form.value.countries_raw
    .split(',')
    .map((c) => c.trim().toUpperCase())
    .filter((c) => c.length === 2)

  if (countries.length > 0) rules.countries = countries

  return Object.keys(rules).length > 0 ? rules : null
}

function debouncedEstimate() {
  if (estimateTimer !== null) clearTimeout(estimateTimer)
  estimateTimer = setTimeout(() => {
    fetchEstimate()
  }, 600)
}

async function fetchEstimate(): Promise<void> {
  estimating.value = true
  try {
    const rules = buildSegmentRules()
    const response = await apiClient.get('/admin/api/push-campaigns/estimate', {
      params: rules ? { segment_rules: rules } : {},
    })
    estimatedAudience.value = (response.data as { estimated_audience: number }).estimated_audience
  } catch {
    estimatedAudience.value = 0
  } finally {
    estimating.value = false
  }
}

async function saveCampaign(): Promise<void> {
  if (!form.value.title.trim() || !form.value.body.trim()) return
  saving.value = true
  try {
    const payload: Record<string, unknown> = {
      title: form.value.title,
      body: form.value.body,
      segment_rules: buildSegmentRules(),
    }
    if (form.value.scheduled_at) payload.scheduled_at = form.value.scheduled_at

    await apiClient.post('/admin/api/push-campaigns', payload)
    toast.success(t('admin.notifications.toast_draft_saved'))
    closeComposeModal()
    await fetchCampaigns(1)
    await fetchStats()
  } catch {
    toast.error(t('admin.notifications.error_save'))
  } finally {
    saving.value = false
  }
}

// ---------------------------------------------------------------
// Send modal helpers
// ---------------------------------------------------------------

async function openSendModal(campaign: PushCampaign): Promise<void> {
  campaignToSend.value = campaign
  sendEstimate.value = 0
  showSendModal.value = true

  // Fetch audience estimate based on campaign's segment_rules
  try {
    const rules = campaign.segment_rules ?? {}
    const response = await apiClient.get('/admin/api/push-campaigns/estimate', {
      params: Object.keys(rules).length > 0 ? { segment_rules: rules } : {},
    })
    sendEstimate.value = (response.data as { estimated_audience: number }).estimated_audience
  } catch {
    sendEstimate.value = 0
  }
}

async function confirmSend(): Promise<void> {
  if (!campaignToSend.value) return
  sending.value = true
  try {
    await apiClient.post(`/admin/api/push-campaigns/${campaignToSend.value.id}/send`)
    toast.success(t('admin.notifications.toast_sent'))
    showSendModal.value = false
    await fetchCampaigns(meta.value.current_page)
    await fetchStats()
  } catch {
    toast.error(t('admin.notifications.error_send'))
  } finally {
    sending.value = false
  }
}

// ---------------------------------------------------------------
// Delete
// ---------------------------------------------------------------

async function deleteCampaign(campaign: PushCampaign): Promise<void> {
  if (!confirm(t('admin.notifications.delete_confirm', { title: campaign.title }))) return
  if (deletingId.value !== null) return
  deletingId.value = campaign.id
  try {
    await apiClient.delete(`/admin/api/push-campaigns/${campaign.id}`)
    toast.success(t('admin.notifications.toast_deleted', { title: campaign.title }))
    await fetchCampaigns(meta.value.current_page)
    await fetchStats()
  } catch {
    toast.error(t('admin.notifications.error_delete'))
  } finally {
    deletingId.value = null
  }
}

// ---------------------------------------------------------------
// Analytics modal
// ---------------------------------------------------------------

async function openAnalyticsModal(campaign: PushCampaign): Promise<void> {
  analyticsData.value = null
  showAnalyticsModal.value = true
  analyticsLoading.value = true
  try {
    const response = await apiClient.get(`/admin/api/push-campaigns/${campaign.id}/analytics`)
    analyticsData.value = response.data as CampaignAnalytics
  } catch {
    toast.error(t('admin.notifications.error_analytics'))
    showAnalyticsModal.value = false
  } finally {
    analyticsLoading.value = false
  }
}

function rateColorClass(rate: number): string {
  if (rate > 50) return 'analytics-rate-green'
  if (rate > 20) return 'analytics-rate-yellow'
  return 'analytics-rate-red'
}

// ---------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------

async function fetchCampaigns(page: number): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string | number> = { page, per_page: 15 }
    if (filterStatus.value) params.status = filterStatus.value

    const response = await apiClient.get('/admin/api/push-campaigns', { params })
    campaigns.value = response.data.data as PushCampaign[]
    meta.value = {
      current_page: response.data.current_page as number,
      last_page: response.data.last_page as number,
      per_page: response.data.per_page as number,
      total: response.data.total as number,
    }
  } catch {
    toast.error(t('admin.notifications.error_load'))
  } finally {
    loading.value = false
  }
}

async function fetchStats(): Promise<void> {
  try {
    const [allRes, sentRes, draftRes] = await Promise.all([
      apiClient.get('/admin/api/push-campaigns', { params: { per_page: 1 } }),
      apiClient.get('/admin/api/push-campaigns', { params: { status: 'sent', per_page: 1 } }),
      apiClient.get('/admin/api/push-campaigns', { params: { status: 'draft', per_page: 1 } }),
    ])

    const allCampaigns = campaigns.value
    const totalDelivered = allCampaigns.reduce((sum, c) => sum + c.delivered_count, 0)

    stats.value = {
      total: allRes.data.total as number,
      sent: sentRes.data.total as number,
      draft: draftRes.data.total as number,
      totalDelivered,
    }
  } catch {
    // non-critical
  }
}

// ---------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------

function truncate(text: string, length: number): string {
  if (text.length <= length) return text
  return text.slice(0, length) + '…'
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'draft':
      return 'badge-draft'
    case 'sending':
      return 'badge-sending'
    case 'sent':
      return 'badge-sent'
    case 'failed':
      return 'badge-failed'
    default:
      return 'badge-draft'
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

onMounted(() => {
  fetchCampaigns(1)
  fetchStats()
})
</script>

<style scoped>
.push-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background: #00cc01;
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-ghost:hover {
  background: var(--bg-tertiary, #213743);
}

/* Stat Grid */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
}

.stat-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Filters */
.filters-row {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
}

.filter-select option {
  background: var(--bg-secondary, #1a2c38);
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

tbody tr {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
}

tbody tr:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.campaign-title {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 13px;
  display: block;
}

.campaign-body {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 2px;
}

.td-num {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

.td-date {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge-draft {
  background: rgba(127, 143, 164, 0.15);
  color: var(--text-tertiary, #7f8fa4);
}

.badge-sending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-sent {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-failed {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Action buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-action-send:hover {
  background: rgba(20, 117, 225, 0.15);
  color: #5ba7f0;
  border-color: rgba(20, 117, 225, 0.3);
}

.btn-action-danger:hover {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  border-color: rgba(237, 66, 69, 0.3);
}

/* Empty row */
.empty-row {
  text-align: center;
  padding: 48px 20px !important;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled):not(.active) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-sm {
  max-width: 420px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  background: transparent;
  border: none;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  transition: color 150ms ease;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

/* Form elements */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-input,
.form-select,
.form-textarea {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 150ms ease;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-select option {
  background: var(--bg-secondary, #1a2c38);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-section-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

/* Audience estimate */
.audience-estimate {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(20, 117, 225, 0.08);
  border: 1px solid rgba(20, 117, 225, 0.2);
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
}

.audience-icon {
  font-size: 18px;
}

.audience-value {
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.audience-label {
  color: var(--text-secondary, #b1bad3);
}

/* Confirm modal */
.confirm-text {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.6;
  margin: 0;
}

.confirm-warning {
  font-size: 12px;
  color: var(--color-warning, #ffa726);
  margin: 0;
}

/* Spinners */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-xs {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(0, 231, 1, 0.25);
  border-top-color: var(--accent-primary, #00e701);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-xs-danger {
  border-color: rgba(237, 66, 69, 0.25);
  border-top-color: var(--color-danger, #ed4245);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Analytics action button */
.btn-action-analytics:hover {
  background: rgba(0, 231, 1, 0.12);
  color: var(--color-success, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

/* Analytics modal */
.modal-analytics {
  max-width: 640px;
}

.modal-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 4px 0 0;
}

.analytics-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.analytics-card {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  text-align: center;
}

.analytics-count {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.analytics-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
}

.analytics-rate {
  font-size: 18px;
  font-weight: 700;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  display: inline-block;
}

.analytics-rate-neutral {
  color: var(--text-tertiary, #7f8fa4);
}

.analytics-rate-green {
  background: rgba(0, 231, 1, 0.12);
  color: var(--color-success, #00e701);
}

.analytics-rate-yellow {
  background: rgba(255, 167, 38, 0.12);
  color: var(--color-warning, #ffa726);
}

.analytics-rate-red {
  background: rgba(237, 66, 69, 0.12);
  color: var(--color-danger, #ed4245);
}

.analytics-legend {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 16px;
  justify-content: center;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-green { background: var(--color-success, #00e701); }
.legend-yellow { background: var(--color-warning, #ffa726); }
.legend-red { background: var(--color-danger, #ed4245); }

/* Responsive */
@media (max-width: 1200px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .push-page {
    padding: 16px;
  }

  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .analytics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
